// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Cart } from "@insite/client-framework/Services/CartService";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { nullPage } from "@insite/client-framework/Store/Data/Pages/PagesState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xHasRoles } from "@werner/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import { USER_ROLES } from "@werner/XDL/constants";

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const xisCheckoutDisabled = (cart: Cart | undefined) => {
    return (
        !canCheckoutWithCart(cart) ||
        hasOnlyQuoteRequiredProducts(cart) ||
        isCartCheckoutDisabled(cart) ||
        (!cart?.paymentOptions?.paymentMethods?.length && !isPunchOutOrder(cart) && !cart?.requiresApproval)
    );
};

export const xIsAdministrator = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return xIsAuthenticated(state) && session.userRoles?.toLowerCase().indexOf("administrator") !== -1;
};

export const getProductList = (state: ApplicationState) => {
    return getPageStateByType(state, "ProductListPage").value || nullPage;
};

export const xGetProductListDataView = (state: ApplicationState, param?: GetProductCollectionApiV2Parameter) => {
    const { parameter, lastParameter } = state.pages.productList;
    let productsDataView = getProductsDataView(state, { ...parameter, ...param });
    if (!productsDataView.value) {
        productsDataView = getProductsDataView(state, { ...lastParameter, ...param });
    }

    return productsDataView;
};

export const getIsPreviouslyPurchasedPage = (state: ApplicationState) => {
    const pageType = getCurrentPage(state).type;
    return pageType === "PreviouslyPurchasedProductsPage";
};

export const canSeeBillTrustLink = (state: ApplicationState): boolean | undefined => {
    return xHasRoles(state, [USER_ROLES.ADMIN, USER_ROLES.FINANCE], "one-or-more");
};

export const getCustomActiveShipTo = (state: ApplicationState): ShipToModel | undefined => {
    return (state as CustomAppState).xAddresses.activeShipTo;
};

export const canGenerateAutoList = (state: ApplicationState) => {
    const { customSettings } = getSettingsCollection(state);
    const restrictedCustomers = customSettings.wernerGenerateListCustomerRestriction;
    const session = getSession(state);
    if (restrictedCustomers?.toLowerCase() === "all") {
        return true;
    }
    if (restrictedCustomers && session) {
        const billTo = session.billToId ? state.data.billTos.byId[session.billToId] : null;
        return !!billTo && restrictedCustomers.split(",").some(customer => customer === billTo.customerNumber);
    }
    return false;
};

export const isRestrictedToShipwhenComplete = (state: ApplicationState, customerNumber?: string) => {
    const { customersWithCallWhenComplete } = getSettingsCollection(state).customSettings;
    const session = getSession(state);
    customerNumber = customerNumber || getBillToState(state, session.billToId).value?.customerNumber;
    return !!customerNumber && customersWithCallWhenComplete.split(",").includes(customerNumber);
};
